// src/hooks/useAlpacaAccountStatus.js
//
// Polls the new /api/account/alpaca/status endpoint every 30 seconds and
// returns account-level state for all stock bots in one shared blob.
//
// Returns:
//   {
//     loading,        // true on first fetch
//     error,          // string or null
//     statusByExchangeId, // { [exchange_id]: { ... per-account state ... } }
//     marketStatus,   // 'regular' | 'pre' | 'post' | 'closed' | 'holiday' (global)
//     nextOpen,       // ms timestamp or null
//     nextClose,      // ms timestamp
//     haltedSymbols,  // [{symbol, reason, resumed_at}] OR [] (global; not per-account)
//     refresh,        // manual trigger
//   }
//
// Each value in statusByExchangeId looks like:
//   {
//     buyingPower: number,     // Alpaca buying_power
//     cash: number,
//     equity: number,
//     status, tradingBlocked, accountBlocked, tradeSuspendedByUser,
//     shortingEnabled, accountType: 'cash'|'margin'|null, multiplier,
//     optionsApprovedLevel, polledAt (ms), lastSyncedAt,
//   }
// (No PDT state: FINRA retired the rule 2026-06-04 and Alpaca removed the
// day-trade fields; the api sends daytradeCount/pdtBlocked/dtBuyingPower as
// null/false for one release only — nothing here reads them.)
//
// Why one endpoint for everything?
//   - Market hours: 1 number per market, global
//   - Halts: ~5-20 symbols at most, global
//   - Per-account buying power + restrictions: changes infrequently (~once per fill)
//   - All three are needed by the same bot cards, so one fetch = one cache entry

import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASEURL;
const POLL_INTERVAL_MS = 30_000;

/**
 * The same GET the hook below makes, as a plain function: the redesigned pages
 * read it through the shared cache (ui/data/queries useAlpacaStatusQuery).
 * Resolves to the response body.
 */
export async function fetchAlpacaAccountStatus() {
  const resp = await axios.get(`${API_BASE}/api/account/alpaca/status`, {
    headers: { Authorization: localStorage.getItem('token') },
    timeout: 8000,
  });
  return resp.data || {};
}

export function useAlpacaAccountStatus({ enabled = true } = {}) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    statusByExchangeId: {},
    marketStatus: null,
    nextOpen: null,
    nextClose: null,
    haltedSymbols: [],
  });

  const inFlightRef = useRef(false);
  const intervalRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const resp = await axios.get(`${API_BASE}/api/account/alpaca/status`, {
        headers: { Authorization: localStorage.getItem('token') },
        timeout: 8000,
      });

      const data = resp.data || {};

      setState({
        loading: false,
        error: null,
        statusByExchangeId: data.accounts || {},
        marketStatus: data.marketStatus || null,
        nextOpen: data.nextOpen || null,
        nextClose: data.nextClose || null,
        haltedSymbols: Array.isArray(data.haltedSymbols) ? data.haltedSymbols : [],
      });
    } catch (err) {
      // Don't blank existing state on transient failure — leave last-good values
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch account status';
      setState(prev => ({ ...prev, loading: false, error: msg }));
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, fetchStatus]);

  return { ...state, refresh: fetchStatus };
}


// Helper: resolve per-account status for a specific bot
export function getAccountStatusForBot(bot, hookResult) {
  if (!bot || !hookResult) return null;

  const exId = bot.exchangeId ?? bot.exchange_id;
  if (!exId) return null;

  const accountState = hookResult.statusByExchangeId?.[exId] || {};

  // Combine per-account + global state into one blob the UI can read
  return {
    ...accountState,
    marketStatus: hookResult.marketStatus,
    nextOpen: hookResult.nextOpen,
    nextClose: hookResult.nextClose,
    haltedSymbols: hookResult.haltedSymbols,
  };
}